import { gql } from '@apollo/client';
import apolloClient from '../../../apolloClient';

export interface NpnData {
  _id: string;
  firstName: string;
  lastName: string;

}

export interface AorData {
  _id: string;
  firstName: string;
  lastName: string;
}

// GraphQL queries
const GET_NPN_BY_OFFICE_ID = gql`
  query GetNPNByOfficeId($officeId: ID!) {
    getNPNByOfficeId(officeId: $officeId) {
      _id
      firstName
      lastName
      
    }
  }
`;

const GET_AOR_BY_OFFICE_ID = gql`
  query GetAORByOfficeId($officeId: ID!) {
    getAORByOfficeId(officeId: $officeId) {
      _id
      firstName
      lastName
    }
  }
`;

// API Response interfaces
interface ApiResponse<T> {
  message: string;
  data: T;
}

class BackOfficeService {
  /**
   * Fetches NPNs for the office
   * @param officeId - The office ID to fetch NPNs for
   * @returns Promise containing array of NPN data
   */
  static async getNpns(officeId: string): Promise<NpnData[]> {
    try {
      const response = await apolloClient.query({
        query: GET_NPN_BY_OFFICE_ID,
        variables: { officeId },
        fetchPolicy: 'network-only'
      });
      
      if (response.data && response.data.getNPNByOfficeId && Array.isArray(response.data.getNPNByOfficeId)) {
        return response.data.getNPNByOfficeId;
      }
      
      throw new Error('Invalid response format for NPNs');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
     // console.error('Error fetching NPNs:', error);
      
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message || error.response.data.error || 'Failed to fetch NPNs';
        throw new Error(errorMessage);
      }
      
      throw new Error('Network error occurred while fetching NPNs');
    }
  }

  /**
   * Fetches AORs for the office
   * @param officeId - The office ID to fetch AORs for
   * @returns Promise containing array of AOR data
   */
  static async getAors(officeId: string): Promise<AorData[]> {
    try {
      const response = await apolloClient.query({
        query: GET_AOR_BY_OFFICE_ID,
        variables: { officeId },
        fetchPolicy: 'network-only'
      });
      
      if (response.data && response.data.getAORByOfficeId && Array.isArray(response.data.getAORByOfficeId)) {
        return response.data.getAORByOfficeId;
      }
      
      throw new Error('Invalid response format for AORs');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
     // console.error('Error fetching AORs:', error);
      
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message || error.response.data.error || 'Failed to fetch AORs';
        throw new Error(errorMessage);
      }
      
      throw new Error('Network error occurred while fetching AORs');
    }
  }

  /**
   * Fetches both NPNs and AORs for the office
   * @param officeId - The office ID to fetch data for
   * @returns Promise containing both NPN and AOR data
   */
  static async getBackOfficeData(officeId: string): Promise<{ npns: NpnData[]; aors: AorData[] }> {
    try {
      const [npnsResponse, aorsResponse] = await Promise.all([
        this.getNpns(officeId),
        this.getAors(officeId)
      ]);

      return {
        npns: npnsResponse,
        aors: aorsResponse
      };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error fetching back office data:', error);
      throw error;
    }
  }
}

export default BackOfficeService;